import { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import ProductCard from "./ProductCard";
import EditPostPopup from "../../editPostPopup";
import "./style.css";
import axios from "axios";

export default function EShopFeed() {
  const { user } = useSelector((state) => ({ ...state }));
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [category, setCategory] = useState("all");
  const [sortBy, setSortBy] = useState("recent");
  const [showFilters, setShowFilters] = useState(false);
  const [cart, setCart] = useState([]);
  const [showCart, setShowCart] = useState(false);
  const [quickViewPost, setQuickViewPost] = useState(null);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [editingPost, setEditingPost] = useState(null);

  // Edit/Delete handlers
  const handleEditPost = (post) => {
    setQuickViewPost(null);
    setEditingPost(post);
  };
  const handlePostUpdated = (updatedPost) => {
    setPosts(prev => prev.map(p => p._id === updatedPost._id ? updatedPost : p));
    setEditingPost(null);
  };
  const handlePostDeleted = (postId) => {
    setPosts(prev => prev.filter(p => p._id !== postId));
    setQuickViewPost(null);
  };

  const categories = [
    { id: "all", name: "Todos", icon: "🛍️" },
    { id: "electronics", name: "Eletrônicos", icon: "📱" },
    { id: "fashion", name: "Moda", icon: "👕" },
    { id: "home", name: "Casa", icon: "🏠" },
    { id: "sports", name: "Esportes", icon: "⚽" },
    { id: "beauty", name: "Beleza", icon: "💄" },
    { id: "toys", name: "Brinquedos", icon: "🧸" },
    { id: "books", name: "Livros", icon: "📚" },
  ];

  const fetchPosts = async (pageNum = 1) => {
    try {
      const categoryParam = category !== "all" ? `&category=${category}` : "";
      const { data } = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/feed/eshop?page=${pageNum}&limit=20&sort=${sortBy}${categoryParam}`,
        {
          headers: {
            Authorization: `Bearer ${user.token}`,
          },
        }
      );
      if (pageNum === 1) {
        setPosts(data.posts || []);
      } else {
        setPosts((prev) => [...prev, ...(data.posts || [])]);
      }
      const pagination = data.pagination || {};
      setHasMore(pagination.page < pagination.pages);
      setLoading(false);
    } catch (error) {
      console.error("Error fetching E-Shop feed:", error);
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPosts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [category]);

  const addToCart = (post) => {
    const existingItem = cart.find((item) => item._id === post._id);
    if (existingItem) {
      setCart(
        cart.map((item) =>
          item._id === post._id
            ? { ...item, quantity: item.quantity + 1 }
            : item
        )
      );
    } else {
      setCart([...cart, { ...post, quantity: 1 }]);
    }
    setShowCart(true);
  };

  const removeFromCart = (postId) => {
    setCart(cart.filter((item) => item._id !== postId));
  };

  const updateQuantity = (postId, quantity) => {
    if (quantity <= 0) {
      removeFromCart(postId);
      return;
    }
    setCart(
      cart.map((item) =>
        item._id === postId ? { ...item, quantity } : item
      )
    );
  };

  const getCartTotal = () => {
    return cart.reduce((total, item) => {
      const price = item.eshopData?.price?.amount || 0;
      return total + price * item.quantity;
    }, 0);
  };

  const formatPrice = (amount) => {
    return new Intl.NumberFormat("pt-BR", {
      style: "currency",
      currency: "BRL",
    }).format(amount);
  };

  const loadMore = () => {
    if (hasMore && !loading) {
      const nextPage = page + 1;
      setPage(nextPage);
      fetchPosts(nextPage);
    }
  };

  if (loading && posts.length === 0) {
    return (
      <div className="eshop_feed_loading">
        <div className="eshop_loader"></div>
        <span>Carregando produtos...</span>
      </div>
    );
  }

  return (
    <div className="eshop_feed">
      {/* Sidebar Categories */}
      <aside className={`eshop_sidebar ${showFilters ? "open" : ""}`}>
        <div className="eshop_sidebar_header">
          <h3>Categorias</h3>
          <button
            className="eshop_close_filters"
            onClick={() => setShowFilters(false)}
          >
            ✕
          </button>
        </div>
        <ul className="eshop_categories">
          {categories.map((cat) => (
            <li
              key={cat.id}
              className={category === cat.id ? "active" : ""}
              onClick={() => {
                setCategory(cat.id);
                setPage(1);
              }}
            >
              <span className="eshop_cat_icon">{cat.icon}</span>
              <span>{cat.name}</span>
            </li>
          ))}
        </ul>

        <div className="eshop_filters">
          <h4>Ordenar por</h4>
          <select value={sortBy} onChange={(e) => setSortBy(e.target.value)}>
            <option value="recent">Mais Recentes</option>
            <option value="price_low">Menor Preço</option>
            <option value="price_high">Maior Preço</option>
            <option value="popular">Mais Vendidos</option>
            <option value="rating">Melhor Avaliados</option>
          </select>
        </div>
      </aside>

      {/* Main Content */}
      <main className="eshop_main">
        {/* Products Grid */}
        {posts.length === 0 ? (
          <div className="eshop_feed_empty">
            <svg viewBox="0 0 24 24" fill="currentColor" width="64" height="64">
              <path d="M7 18c-1.1 0-1.99.9-1.99 2S5.9 22 7 22s2-.9 2-2-.9-2-2-2zM1 2v2h2l3.6 7.59-1.35 2.45c-.16.28-.25.61-.25.96 0 1.1.9 2 2 2h12v-2H7.42c-.14 0-.25-.11-.25-.25l.03-.12.9-1.63h7.45c.75 0 1.41-.41 1.75-1.03l3.58-6.49c.08-.14.12-.31.12-.48 0-.55-.45-1-1-1H5.21l-.94-2H1zm16 16c-1.1 0-1.99.9-1.99 2s.89 2 1.99 2 2-.9 2-2-.9-2-2-2z" />
            </svg>
            <h3>Nenhum produto encontrado</h3>
            <p>Seja o primeiro a vender algo!</p>
          </div>
        ) : (
          <>
            <div className="eshop_grid">
              {posts.map((post) => (
                <ProductCard
                  key={post._id}
                  post={post}
                  user={user}
                  onAddToCart={addToCart}
                  onQuickView={setQuickViewPost}
                  onEdit={handleEditPost}
                  onPostDeleted={handlePostDeleted}
                />
              ))}
            </div>

            {hasMore && (
              <div className="eshop_load_more">
                <button onClick={loadMore} disabled={loading}>
                  {loading ? "Carregando..." : "Ver mais produtos"}
                </button>
              </div>
            )}
          </>
        )}
      </main>

      {/* Cart Sidebar */}
      <aside className={`eshop_cart ${showCart ? "open" : ""}`}>
        <div className="eshop_cart_header">
          <h3>Carrinho ({cart.length})</h3>
          <button onClick={() => setShowCart(false)}>✕</button>
        </div>

        {cart.length === 0 ? (
          <div className="eshop_cart_empty">
            <svg viewBox="0 0 24 24" fill="currentColor" width="48" height="48">
              <path d="M7 18c-1.1 0-1.99.9-1.99 2S5.9 22 7 22s2-.9 2-2-.9-2-2-2zM1 2v2h2l3.6 7.59-1.35 2.45c-.16.28-.25.61-.25.96 0 1.1.9 2 2 2h12v-2H7.42c-.14 0-.25-.11-.25-.25l.03-.12.9-1.63h7.45c.75 0 1.41-.41 1.75-1.03l3.58-6.49c.08-.14.12-.31.12-.48 0-.55-.45-1-1-1H5.21l-.94-2H1zm16 16c-1.1 0-1.99.9-1.99 2s.89 2 1.99 2 2-.9 2-2-.9-2-2-2z" />
            </svg>
            <p>Seu carrinho está vazio</p>
          </div>
        ) : (
          <>
            <div className="eshop_cart_items">
              {cart.map((item) => (
                <div key={item._id} className="eshop_cart_item">
                  <img
                    src={item.images?.[0]?.url || item.images?.[0]}
                    alt=""
                  />
                  <div className="eshop_cart_item_info">
                    <h4>{item.eshopData?.productName || item.text}</h4>
                    <span className="eshop_cart_item_price">
                      {formatPrice(item.eshopData?.price?.amount || 0)}
                    </span>
                    <div className="eshop_cart_item_quantity">
                      <button
                        onClick={() =>
                          updateQuantity(item._id, item.quantity - 1)
                        }
                      >
                        -
                      </button>
                      <span>{item.quantity}</span>
                      <button
                        onClick={() =>
                          updateQuantity(item._id, item.quantity + 1)
                        }
                      >
                        +
                      </button>
                    </div>
                  </div>
                  <button
                    className="eshop_cart_remove"
                    onClick={() => removeFromCart(item._id)}
                  >
                    ✕
                  </button>
                </div>
              ))}
            </div>

            <div className="eshop_cart_footer">
              <div className="eshop_cart_total">
                <span>Total:</span>
                <strong>{formatPrice(getCartTotal())}</strong>
              </div>
              <button className="eshop_checkout_btn">
                Finalizar Compra
              </button>
            </div>
          </>
        )}
      </aside>

      {/* Cart Toggle Button */}
      <button
        className="eshop_cart_toggle"
        onClick={() => setShowCart(!showCart)}
      >
        <svg viewBox="0 0 24 24" fill="currentColor" width="24" height="24">
          <path d="M7 18c-1.1 0-1.99.9-1.99 2S5.9 22 7 22s2-.9 2-2-.9-2-2-2zM1 2v2h2l3.6 7.59-1.35 2.45c-.16.28-.25.61-.25.96 0 1.1.9 2 2 2h12v-2H7.42c-.14 0-.25-.11-.25-.25l.03-.12.9-1.63h7.45c.75 0 1.41-.41 1.75-1.03l3.58-6.49c.08-.14.12-.31.12-.48 0-.55-.45-1-1-1H5.21l-.94-2H1zm16 16c-1.1 0-1.99.9-1.99 2s.89 2 1.99 2 2-.9 2-2-.9-2-2-2z" />
        </svg>
        {cart.length > 0 && (
          <span className="eshop_cart_badge">{cart.length}</span>
        )}
      </button>

      {/* Quick View Modal */}
      {quickViewPost && (
        <div className="eshop_quick_view_overlay" onClick={() => setQuickViewPost(null)}>
          <div className="eshop_quick_view" onClick={(e) => e.stopPropagation()}>
            <button
              className="eshop_quick_view_close"
              onClick={() => setQuickViewPost(null)}
            >
              ✕
            </button>
            <div className="eshop_quick_view_images">
              <img
                src={quickViewPost.images?.[0]?.url || quickViewPost.images?.[0]}
                alt=""
              />
            </div>
            <div className="eshop_quick_view_info">
              <h2>{quickViewPost.eshopData?.productName || quickViewPost.text}</h2>
              <p className="eshop_quick_view_desc">
                {quickViewPost.eshopData?.description || "Sem descrição"}
              </p>
              <div className="eshop_quick_view_price">
                {formatPrice(quickViewPost.eshopData?.price?.amount || 0)}
              </div>
              <button
                className="eshop_add_to_cart_large"
                onClick={() => {
                  addToCart(quickViewPost);
                  setQuickViewPost(null);
                }}
              >
                Adicionar ao Carrinho
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Post Modal */}
      {editingPost && (
        <EditPostPopup
          post={editingPost}
          user={user}
          setVisible={() => setEditingPost(null)}
          onPostUpdated={handlePostUpdated}
        />
      )}
    </div>
  );
}
